/**
 * Skeleton: the shape of what is coming.
 *
 * LOADING IS NOT A SENTENCE. "Loading strategies…" tells the reader nothing
 * about what will arrive or where; a skeleton has the layout of the real
 * thing, so nothing jumps when it lands. The rules:
 *
 *   - Build the skeleton from the same grid as the content it stands in for:
 *     a table's skeleton is rows with the table's columns.
 *   - Nothing is drawn for the first 200ms. A fast answer never flashes.
 *     `SkeletonGroup` applies that delay to everything inside it.
 *   - Work of a KNOWN length is not a skeleton: show a `ProgressBar`, a time
 *     and Cancel.
 *
 * The shimmer is one of the three things allowed to loop (DECISION rule 8) and
 * stops under `prefers-reduced-motion`, leaving the shapes.
 *
 * Shapes are decoration. `SkeletonGroup` is what a screen reader hears: one
 * polite "Loading strategies", once, and `aria-busy` until the content
 * replaces it.
 */
import type { CSSProperties, HTMLAttributes } from "react";
import { cx } from "./cx.js";
import "./skeleton.css";

export interface SkeletonProps extends HTMLAttributes<HTMLSpanElement> {
  /** `text` is one line of type; `block` is a tile or a chip; `circle` an avatar. */
  shape?: "text" | "block" | "circle";
  /** Any CSS length. Text defaults to 100% of its container. */
  width?: number | string;
  height?: number | string;
}

const length = (value: number | string | undefined): string | undefined =>
  typeof value === "number" ? `${value / 16}rem` : value;

export function Skeleton({ shape = "text", width, height, className, style, ...rest }: SkeletonProps) {
  const sized: CSSProperties = { ...style };
  const w = length(width);
  const h = length(height);
  if (w !== undefined) sized.width = w;
  if (h !== undefined) sized.height = h;
  return (
    <span
      aria-hidden="true"
      {...rest}
      data-shape={shape}
      style={sized}
      className={cx("mdl-skeleton", className)}
    />
  );
}

export interface SkeletonGroupProps extends HTMLAttributes<HTMLDivElement> {
  /** What is loading, for a screen reader: "Loading strategies". */
  label: string;
  /** Show at once, skipping the 200ms grace. For a region already known to be slow. */
  immediate?: boolean;
}

export function SkeletonGroup({ label, immediate = false, className, children, ...rest }: SkeletonGroupProps) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label={label}
      {...rest}
      data-immediate={immediate ? "true" : undefined}
      className={cx("mdl-skeleton-group", className)}
    >
      {children}
    </div>
  );
}
